import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, Plus, Trash, Image as ImageIcon } from "@phosphor-icons/react";
import { api, API, formatApiErrorDetail } from "../../lib/api";
import { QuestionsEditor } from "./QuestionsEditor";

export const SpeakingManager = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const input = useRef(null);

  const toForm = (d) => ({ ...d, parameters: (d.parameters || []).map((p) => ({ label: p.label, keywords: (p.keywords || []).join(", ") })) });
  useEffect(() => { api.get("/admin/speaking").then((r) => setCfg(toForm(r.data))).catch(() => toast.error("Could not load speaking task")); }, []);

  if (!cfg) return <div className="font-mono-fig text-sm text-muted-foreground p-6">Loading…</div>;

  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const setParam = (i, k, v) => setCfg((c) => ({ ...c, parameters: c.parameters.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/speaking/image", fd);
      setCfg((c) => ({ ...c, has_image: data.has_image, image_version: data.image_version }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        prompt: cfg.prompt, description: cfg.description, enabled: cfg.enabled,
        min_words: Number(cfg.min_words), target_words: Number(cfg.target_words),
        questions: cfg.questions.filter((q) => q.trim()), questions_enabled: cfg.questions_enabled,
        parameters: cfg.parameters.filter((p) => p.label.trim()).map((p) => ({ label: p.label, keywords: p.keywords.split(",") })),
      };
      const { data } = await api.put("/admin/speaking", body);
      setCfg(toForm(data));
      toast.success("Speaking task saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8 neu p-6 md:p-8" data-testid="admin-speaking-manager">
      <div className="overline">Speaking task 1 · one picture for every test</div>
      <p className="mt-2 text-sm text-foreground/75 max-w-3xl">
        Learners describe this picture out loud (up to 2 minutes, 3 tries). Their speech is turned into text and checked against your
        parameters: each parameter is met if the learner says at least one of its keywords. Without an image, the speaking section is skipped.
      </p>
      <div className="mt-6 grid lg:grid-cols-2 gap-8">
        <div>
          {cfg.has_image ? (
            <img src={`${API}/speaking/image?v=${cfg.image_version}`} alt="Speaking task" className="w-full border border-foreground" data-testid="admin-speaking-image" />
          ) : (
            <div className="aspect-video neu-inset flex items-center justify-center text-muted-foreground"><ImageIcon size={40} /></div>
          )}
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="admin-speaking-image-input" />
          <button onClick={() => input.current?.click()} disabled={uploading} data-testid="admin-speaking-upload-btn" className="neu-btn mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm">
            <UploadSimple size={14} weight="bold" /> {uploading ? "Uploading…" : cfg.has_image ? "Replace image" : "Upload image"}
          </button>
          <label className="mt-6 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={cfg.enabled} onChange={set("enabled")} className="accent-[#ff8000] w-4 h-4" data-testid="admin-speaking-enabled" />
            Include the speaking task in new tests
          </label>
        </div>
        <div className="space-y-5">
          <div>
            <label className="overline">Instruction shown to the learner</label>
            <textarea value={cfg.prompt} onChange={set("prompt")} rows={3} className="neu-input w-full mt-2 p-3 text-sm" data-testid="admin-speaking-prompt" />
          </div>
          <div>
            <label className="overline">Image description (only for admins)</label>
            <textarea value={cfg.description} onChange={set("description")} rows={3} className="neu-input w-full mt-2 p-3 text-sm" data-testid="admin-speaking-description" />
          </div>
          <div>
            <label className="overline">Parameters · label + keywords (comma separated)</label>
            <div className="mt-2 space-y-2">
              {cfg.parameters.map((p, i) => (
                <div key={i} className="flex flex-col sm:flex-row gap-2" data-testid={`admin-speaking-param-${i}`}>
                  <input aria-label={`Parameter ${i + 1} label`} value={p.label} onChange={(e) => setParam(i, "label", e.target.value)} placeholder="e.g. Mentions the people" className="neu-input flex-1 px-3 py-2 text-sm" />
                  <input aria-label={`Parameter ${i + 1} keywords`} value={p.keywords} onChange={(e) => setParam(i, "keywords", e.target.value)} placeholder="man, woman, children" className="neu-input flex-1 px-3 py-2 text-sm" />
                  <button onClick={() => setCfg((c) => ({ ...c, parameters: c.parameters.filter((_, j) => j !== i) }))} aria-label="Remove parameter" className="neu-btn px-3"><Trash size={14} /></button>
                </div>
              ))}
            </div>
            <button onClick={() => setCfg((c) => ({ ...c, parameters: [...c.parameters, { label: "", keywords: "" }] }))} data-testid="admin-speaking-add-param" className="neu-btn mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs">
              <Plus size={12} weight="bold" /> Add parameter
            </button>
          </div>
          <div className="flex gap-4">
            <label className="text-sm">Minimum words
              <input type="number" value={cfg.min_words} onChange={set("min_words")} className="neu-input w-24 ml-2 px-2 py-1" data-testid="admin-speaking-min-words" />
            </label>
            <label className="text-sm">Target words
              <input type="number" value={cfg.target_words} onChange={set("target_words")} className="neu-input w-24 ml-2 px-2 py-1" data-testid="admin-speaking-target-words" />
            </label>
          </div>
          <button onClick={save} disabled={saving} data-testid="admin-speaking-save-btn" className="neu-btn-primary px-6 py-3 text-sm">
            {saving ? "Saving…" : "Save speaking tasks"}
          </button>
        </div>
      </div>
      <QuestionsEditor cfg={cfg} setCfg={setCfg} />
      <button onClick={save} disabled={saving} data-testid="admin-speaking-save-questions-btn" className="neu-btn-primary mt-6 px-6 py-3 text-sm">
        {saving ? "Saving…" : "Save speaking tasks"}
      </button>
    </div>
  );
};
